import React, { useState } from 'react';
import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { ApprovalRequest, useApproveStepMutation, useRejectStepMutation } from '../../../../../services/ApprovalService';

interface InlineApprovalActionProps {
  approval: ApprovalRequest;
}

export const InlineApprovalAction: React.FC<InlineApprovalActionProps> = ({ approval }) => {
  const [approveStep, approveState] = useApproveStepMutation();
  const [rejectStep, rejectState] = useRejectStepMutation();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submitting = approveState.isLoading || rejectState.isLoading;
  const commentMissing = approval.requireComment && !comment.trim();

  const handleAction = async (action: 'approve' | 'reject') => {
    setError(null);
    const payload = {
      instanceId: approval.workflowInstanceId,
      stepId: approval.stepId,
      approvalRequestId: approval.id,
      comment: comment.trim() || undefined,
    };
    try {
      if (action === 'approve') {
        await approveStep(payload).unwrap();
        setSuccess('Approved successfully.');
      } else {
        await rejectStep(payload).unwrap();
        setSuccess('Rejected.');
      }
    } catch (e) {
      const httpStatus = (e as { status?: number })?.status;
      const msg = (e as { data?: string })?.data;
      setError(
        httpStatus === 403
          ? 'You are not authorized to act on this approval.'
          : typeof msg === 'string' && msg ? msg : 'Action failed.'
      );
    }
  };

  if (success) {
    return <Alert severity="success" sx={{ mt: 1 }}>{success}</Alert>;
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={approval.requireComment ? 'Comment (required)' : 'Comment (optional)'}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        multiline
        maxRows={3}
        sx={{ mb: 1 }}
      />
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          disabled={submitting || commentMissing}
          onClick={() => handleAction('approve')}
        >
          Approve
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<Cancel />}
          disabled={submitting || commentMissing}
          onClick={() => handleAction('reject')}
        >
          Reject
        </Button>
      </Stack>
    </Box>
  );
};
